/* eslint-disable */

/**
 * Plausible Analytics Module
 * Handles event logging to Plausible with data normalization
 * 
 * Normalizes objects to match Plausible requirements:
 * - Converts complex objects to scalar values (strings, numbers, booleans)
 * - Limits keys to 300 characters
 * - Limits values to 2000 characters
 * - Limits total properties to 30
 */

/**
 * Normalize object for Plausible
 */
function normalizePlausibleObject(obj) {
  const result = {};
  
  // Convert object to simple key-value pairs with scalar values only
  const processValue = (value, keyPrefix = '') => {
    if (value === null || value === undefined) {
      return null;
    }
    
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return value; // Return scalar values as is
    }
    
    if (value instanceof Date) {
      return value.toISOString(); // Convert dates to ISO strings
    }
    
    // Skip functions, DOM elements, etc.
    if (typeof value === 'function' || 
        (typeof value === 'object' && value.nodeType) || 
        value === window || 
        value === document) {
      return null;
    }
    
    // For arrays and objects, flatten with key prefixes
    if (typeof value === 'object') {
      Object.keys(value).forEach(key => {
        // Skip circular references and non-enumerable properties
        if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
          return;
        }
        
        const propertyValue = processValue(value[key]);
        if (propertyValue !== null) {
          // For arrays, use index as part of the key
          const newKey = keyPrefix ? `${keyPrefix}_${key}` : key;
          // Limit key length to comply with Plausible's 300 char limit
          const limitedKey = newKey.substring(0, 300);
          
          // Convert value to string and limit to 2000 chars if needed
          let stringValue = propertyValue;
          if (typeof propertyValue === 'object') {
            try {
              stringValue = JSON.stringify(propertyValue).substring(0, 2000);
            } catch (e) {
              stringValue = String(propertyValue).substring(0, 2000);
            }
          } else if (typeof propertyValue === 'string') {
            stringValue = propertyValue.substring(0, 2000);
          }
          
          result[limitedKey] = stringValue;
        }
      });
    }
    
    return null;
  };
  
  // Process top-level keys
  Object.keys(obj).forEach(key => {
    const value = processValue(obj[key]);
    if (value !== null && typeof value !== 'object') {
      result[key] = value;
    } else if (value === null && typeof obj[key] === 'object') {
      // Already processed nested object properties with prefixed keys
    } else {
      result[key] = String(value).substring(0, 2000);
    }
  });
  
  // Ensure we don't exceed Plausible's limit of 30 properties
  const finalResult = {};
  const keys = Object.keys(result).slice(0, 30);
  keys.forEach(key => {
    finalResult[key] = result[key];
  });
  
  return finalResult;
}

/**
 * Log event to Plausible
 */
export function logPlausibleEvent(eventName, eventData, debug = false) {
  if (!window.plausible) {
    if (debug) {
      console.info('[Plausible] Not available');
    }
    return;
  }

  try {
    const normalizedObj = normalizePlausibleObject(eventData);

    plausible(eventName, {
      props: normalizedObj,
    });

    if (debug) {
      console.info(`[Plausible] Event: ${eventName}`);
    }
  } catch (error) {
    console.error('[Plausible] Log failed:', error);
  }
}
